/**
 * ADMIN COMPANIES PAGE
 *
 * Manage all companies in the leBusiness platform
 * - View all companies with license and user info
 * - Filter by status, plan, search
 * - Manage features per company
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Building2,
  Search,
  MoreHorizontal,
  Eye,
  Key,
  Boxes,
  CreditCard,
  Ban,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAdminCompaniesExtended, useAdminCompanyStats } from '@/hooks/queries/useAdminCompaniesExtended';
import { useAdminFeatures, useSetCompanyOverride, useRemoveCompanyOverride } from '@/hooks/queries/useAdminFeatures';
import { FeatureFlagService } from '@/services/featureFlagService';
import { useQuery } from '@tanstack/react-query';
import type { CompanyWithDetails } from '@/services/adminCompanyService';
import type { CompanyFeatureOverride } from '@/services/featureFlagService';

export const Route = createFileRoute('/admin/companies/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminCompaniesPage,
});

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>;
    case 'trial':
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Trial</Badge>;
    case 'expired':
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expired</Badge>;
    case 'suspended':
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Suspended</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const getPlanBadge = (plan: string) => {
  const colors: Record<string, string> = {
    'Trial': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    'Starter': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Professional': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Business': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    'Enterprise': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return <Badge className={colors[plan] || 'bg-slate-500/20 text-slate-400'}>{plan}</Badge>;
};

function ManageFeaturesDialog({
  company,
  onClose,
}: {
  company: CompanyWithDetails;
  onClose: () => void;
}) {
  const { profile } = useAuthStore();
  const { data: allFeatures } = useAdminFeatures();
  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['company-overrides', company.id],
    queryFn: () => FeatureFlagService.getCompanyOverrides(company.id),
    staleTime: 1 * 60 * 1000,
  });
  const setOverride = useSetCompanyOverride();
  const removeOverride = useRemoveCompanyOverride();

  const getOverrideForFeature = (featureId: string): CompanyFeatureOverride | undefined => {
    return overrides?.find((o) => o.feature_id === featureId);
  };

  const handleToggleFeature = (featureId: string, currentOverride: CompanyFeatureOverride | undefined, featureDefault: boolean) => {
    if (!profile) return;

    if (currentOverride) {
      // If there's an override, toggle it or remove it
      if (currentOverride.enabled !== featureDefault) {
        // Override differs from default, toggling should remove it (revert to default)
        removeOverride.mutate({ companyId: company.id, featureId });
      } else {
        // Override matches default, toggling should flip it
        setOverride.mutate({
          companyId: company.id,
          featureId,
          enabled: !currentOverride.enabled,
          createdBy: profile.id,
        });
      }
    } else {
      // No override exists, create one with the opposite of default
      setOverride.mutate({
        companyId: company.id,
        featureId,
        enabled: !featureDefault,
        createdBy: profile.id,
      });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Features: {company.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Toggle features for this company. Overrides are highlighted.
          </DialogDescription>
        </DialogHeader>

        {overridesLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 bg-slate-700" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {(allFeatures || []).map((feature) => {
              const override = getOverrideForFeature(feature.id);
              const isEnabled = override ? override.enabled : feature.default_enabled;
              const hasOverride = !!override;

              return (
                <div
                  key={feature.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    hasOverride ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-slate-800'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{feature.name}</span>
                      {hasOverride && (
                        <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs">
                          Override
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{feature.code}</span>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => handleToggleFeature(feature.id, override, feature.default_enabled)}
                    disabled={setOverride.isPending || removeOverride.isPending}
                  />
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminCompaniesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [managingCompany, setManagingCompany] = useState<CompanyWithDetails | null>(null);

  const filters = {
    search: searchQuery || undefined,
    status: statusFilter as any,
    plan: planFilter !== 'all' ? planFilter : undefined,
  };

  const { data: companies, isLoading } = useAdminCompaniesExtended(filters);
  const { data: stats } = useAdminCompanyStats();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Building2 className="w-8 h-8 text-violet-400" />
              Companies
            </h1>
            <p className="text-slate-400 mt-2">
              Manage all registered companies on the platform
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="border-slate-700 text-slate-300">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.active ?? '-'}</p>
                  <p className="text-sm text-slate-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.trial ?? '-'}</p>
                  <p className="text-sm text-slate-400">Trial</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.expired ?? '-'}</p>
                  <p className="text-sm text-slate-400">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Ban className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.suspended ?? '-'}</p>
                  <p className="text-sm text-slate-400">Suspended</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="Starter">Starter</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="Enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Companies Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 bg-slate-700" />
                ))}
              </div>
            ) : companies && companies.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Users</TableHead>
                    <TableHead className="text-slate-400">License Expiry</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id} className="border-slate-700 hover:bg-slate-700/30">
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{company.name}</p>
                          <p className="text-sm text-slate-400">{company.email || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(company.license?.plan_name || 'None')}</TableCell>
                      <TableCell>{getStatusBadge(company.license?.status || 'expired')}</TableCell>
                      <TableCell className="text-slate-300">{company.users_count}</TableCell>
                      <TableCell className="text-slate-300">
                        {company.license?.expires_at
                          ? new Date(company.license.expires_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-white">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                            <DropdownMenuLabel className="text-slate-400">Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem className="text-slate-300 hover:text-white focus:text-white focus:bg-slate-700">
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-slate-300 hover:text-white focus:text-white focus:bg-slate-700"
                              onClick={() => setManagingCompany(company)}
                            >
                              <Boxes className="w-4 h-4 mr-2" />
                              Manage Features
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-slate-300 hover:text-white focus:text-white focus:bg-slate-700">
                              <Key className="w-4 h-4 mr-2" />
                              Manage License
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-slate-300 hover:text-white focus:text-white focus:bg-slate-700">
                              <CreditCard className="w-4 h-4 mr-2" />
                              Change Plan
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-slate-500">
                No companies found matching your filters.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manage Features Dialog */}
        {managingCompany && (
          <ManageFeaturesDialog
            company={managingCompany}
            onClose={() => setManagingCompany(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminCompaniesPage;
